import { forwardRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { UseQueryResult } from "@tanstack/react-query";
import AsyncSelect, { Option, type AsyncSelectBaseProps } from "./AsyncSelect";
import { EntityListLink } from "./EntityListLink";
import { Module, PATH_DICTS_PROVINCES } from "@/constants";
import { SuccessFetchListResponse } from "@/services/api";
import { fetchProvinceList } from "@/services/province";
import appConfiguration from "./cfg/AppCfg";

interface ProvinceSelectProps extends AsyncSelectBaseProps {
  className?: string;
  inDialog?: boolean;
  showLink?: boolean;
  defaultItem?: Partial<Option>;
  showNfzFooter?: boolean;
}
export default forwardRef<HTMLButtonElement, ProvinceSelectProps>(
  function ProvinceSelect(
    {
      value,
      inDialog,
      showLink = false,
      defaultItem,
      showNfzFooter = false,
      ...props
    },
    ref,
  ) {
    const { t } = useTranslation();

    const renderFooter = useCallback(
      (query: UseQueryResult<SuccessFetchListResponse<Option>, Error>) => {
        if (!showNfzFooter) {
          return null;
        }

        const selectedProvinceData = query.data?.items?.find(
          (item) => item.id === value,
        );
        const code = selectedProvinceData?.nfz_code ?? defaultItem?.nfz_code;
        const description =
          selectedProvinceData?.nfz_description ?? defaultItem?.nfz_description;

        if (!code && !description) {
          return null;
        }

        const nfzText = [description, code].filter(Boolean).join(" | ");

        return (
          <div className="mt-1 text-xs text-gray-500">
            <span className="font-medium">
              {t("provinces.fields.nfz_description")}:{" "}
            </span>
            <span>{nfzText}</span>
          </div>
        );
      },
      [showNfzFooter, value, defaultItem, t],
    );

    return (
      <span className="flex items-start gap-1">
        <span className="flex-1">
          <AsyncSelect
            queryKey={[Module.Provinces]}
            queryFn={() =>
              fetchProvinceList({
                limit: appConfiguration.selectComponentsPaginationLimit,
              })
            }
            enableFilter={false}
            placeholder={t("provinceSelect.placeholder")}
            loadingLabel={t("provinceSelect.loadingProvinces")}
            value={value ? value.toString() : undefined}
            inDialog={inDialog}
            allowClearSelection
            {...props}
            ref={ref}
            className="w-full"
            defaultItem={defaultItem}
            footer={renderFooter}
          />
        </span>
        {showLink && (
          <EntityListLink
            path={PATH_DICTS_PROVINCES}
            label={t("provinceSelect.link")}
          />
        )}
      </span>
    );
  },
);
